import type {
  BlogCategory,
  BlogPost,
  PatchAdminV1StoresStoreIdBlogCategoriesIdBody,
  PatchAdminV1StoresStoreIdBlogPostsIdBody,
  PostAdminV1StoresStoreIdBlogCategoriesBody,
  PostAdminV1StoresStoreIdBlogPostsBody,
} from '@nucleus-commerce/api-client/admin';
import { fromZonedInput, toZonedInput } from '@nucleus-commerce/core';

import { emptySeoOverride, fromSeoOverride, toSeoOverride } from '@/features/seo';

import type { BlogCategoryForm } from '../schemas/blog-category-form-schema';
import type { BlogPostFormInput, BlogPostFormOutput } from '../schemas/blog-post-form-schema';

const orNull = (value: string): string | null => (value.trim() === '' ? null : value);

export function emptyBlogPostForm(): BlogPostFormInput {
  return {
    title: '',
    handle: '',
    excerpt: '',
    bodyHtml: '',
    featuredMediaId: null,
    authorName: '',
    categoryId: '',
    tags: [],
    status: 'draft',
    publishedAt: '',
    seo: emptySeoOverride(),
  };
}

export function fromBlogPost(post: BlogPost, timeZone: string): BlogPostFormInput {
  return {
    title: post.title,
    handle: post.handle,
    excerpt: post.excerpt ?? '',
    bodyHtml: post.bodyHtml ?? '',
    featuredMediaId: post.featuredMedia?.id ?? null,
    authorName: post.authorName ?? '',
    categoryId: post.category?.id ?? '',
    tags: [...post.tags],
    status: post.status,
    publishedAt: toZonedInput(post.publishedAt, timeZone),
    seo: fromSeoOverride(post.seo),
  };
}

/** Every field as the API wants it; create and update both start from here. */
function fullBody(values: BlogPostFormInput, timeZone: string) {
  return {
    handle: values.handle.trim(),
    title: values.title.trim(),
    excerpt: orNull(values.excerpt.trim()),
    bodyHtml: orNull(values.bodyHtml),
    featuredMediaId: values.featuredMediaId,
    authorName: orNull(values.authorName.trim()),
    categoryId: orNull(values.categoryId),
    tags: values.tags,
    status: values.status,
    seo: toSeoOverride(values.seo),
    publishedAt: values.publishedAt ? fromZonedInput(values.publishedAt, timeZone) : null,
  };
}

/** Empty optional fields are left out; the backend defaults them. */
export function toBlogPostCreate(
  values: BlogPostFormOutput,
  timeZone: string,
): PostAdminV1StoresStoreIdBlogPostsBody {
  const body = fullBody(values, timeZone);
  return {
    handle: body.handle,
    title: body.title,
    status: body.status,
    ...(body.excerpt !== null ? { excerpt: body.excerpt } : {}),
    ...(body.bodyHtml !== null ? { bodyHtml: body.bodyHtml } : {}),
    ...(body.featuredMediaId !== null ? { featuredMediaId: body.featuredMediaId } : {}),
    ...(body.authorName !== null ? { authorName: body.authorName } : {}),
    ...(body.categoryId !== null ? { categoryId: body.categoryId } : {}),
    ...(body.tags.length > 0 ? { tags: body.tags } : {}),
    ...(body.seo !== null ? { seo: body.seo } : {}),
    ...(body.publishedAt !== null ? { publishedAt: body.publishedAt } : {}),
  };
}

/**
 * Only what changed against the loaded post travels, so an untouched handle never triggers a
 * redirect and an untouched date keeps its seconds. A cleared publish date is sent as null,
 * which the backend reads as "now" for a published post.
 */
export function toBlogPostUpdate(
  values: BlogPostFormOutput,
  initial: BlogPostFormInput,
  timeZone: string,
): PatchAdminV1StoresStoreIdBlogPostsIdBody {
  const next = fullBody(values, timeZone);
  const before = fullBody(initial, timeZone);
  const body: Record<string, unknown> = {};
  for (const key of Object.keys(next) as Array<keyof typeof next>) {
    if (JSON.stringify(next[key]) !== JSON.stringify(before[key])) body[key] = next[key];
  }
  return body;
}

export function emptyBlogCategoryForm(nextPosition: number): BlogCategoryForm {
  return { title: '', handle: '', description: '', position: String(nextPosition) };
}

export function fromBlogCategory(category: BlogCategory): BlogCategoryForm {
  return {
    title: category.title,
    handle: category.handle,
    description: category.description ?? '',
    position: String(category.position),
  };
}

export function toBlogCategoryCreate(
  values: BlogCategoryForm,
): PostAdminV1StoresStoreIdBlogCategoriesBody {
  const description = values.description.trim();
  const position = values.position.trim();
  return {
    handle: values.handle.trim(),
    title: values.title.trim(),
    ...(description ? { description } : {}),
    ...(position ? { position: Number(position) } : {}),
  };
}

export function toBlogCategoryUpdate(
  values: BlogCategoryForm,
  initial: BlogCategoryForm,
): PatchAdminV1StoresStoreIdBlogCategoriesIdBody {
  const body: PatchAdminV1StoresStoreIdBlogCategoriesIdBody = {};
  if (values.handle.trim() !== initial.handle) body.handle = values.handle.trim();
  if (values.title.trim() !== initial.title) body.title = values.title.trim();
  if (values.description.trim() !== initial.description) {
    body.description = orNull(values.description.trim());
  }
  const position = values.position.trim();
  if (position !== '' && position !== initial.position) body.position = Number(position);
  return body;
}
